<template>
    <div class="max-w-[80%] w-full h-full">
        <h3 class="text-center pb-2 font-semibold">{{ batchTabIsActive ? $t('views.reports.selectSitesPage.selectBatchesConditions') : $t('views.reports.selectSitesPage.selectFarmsConditions') }}</h3>
        <div class="border-2 h-full overflow-y-auto border-opacity-10 rounded-lg py-2 max-h-[50rem]">
            <table class="w-full">
                <thead></thead>
                <tbody v-if="batchTabIsActive">
                    <tr v-for="site of batchSites" :key="site.site_ids" class="hover:bg-gray-100 cursor-pointer" @click="onBatchClicked(site)">
                        <td class="pl-4 font-medium">{{ site.name }}</td>
                        <td class="w-6.5 pr-4">
                            <div class="flex justify-center">
                                <CustomCheckbox :checked="computedState.siteIds.includes(site.site_ids)" @update:checked="onBatchClicked(site)"></CustomCheckbox>
                            </div>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <template v-for="farm of farmSites" :key="getFarmKey(farm)">
                        <tr class="hover:bg-gray-100 cursor-pointer" @click="onFarmClicked(farm)">
                            <td class="pl-4 py-2 font-medium">
                                <span class="inline-flex items-center mr-3">
                                    <button v-if="farm.barns?.length" type="button" class="inline-flex rounded-full p-2.5 hover:bg-gray-200 transition-colors" @click.stop="toggleFarmExpanded(farm)">
                                        <ExpandIcon color="black" :expand="isFarmExpanded(farm)" class="w-3.5 h-3.5" />
                                    </button>
                                    <span v-else class="inline-flex rounded-full p-2.5">
                                        <span class="inline-block w-3.5 h-3.5"></span>
                                    </span>
                                </span>
                                <span>{{ farm.name }}</span>
                            </td>
                            <td class="w-6.5 pr-4 py-2">
                                <div class="flex justify-center">
                                    <CustomCheckbox :checked="isFarmChecked(farm)" :indeterminate="isFarmIndeterminate(farm)" @update:checked="onFarmClicked(farm)"></CustomCheckbox>
                                </div>
                            </td>
                        </tr>
                        <tr>
                            <td colspan="2" class="p-0">
                                <ExpandCollapseTransition>
                                    <div v-show="isFarmExpanded(farm)">
                                        <table class="w-full">
                                            <tbody>
                                                <tr v-for="barn of farm.barns || []" :key="getBarnKey(farm, barn)" class="hover:bg-gray-50 cursor-pointer" @click="onBarnClicked(farm, barn)">
                                                    <td class="pl-16 py-2 text-sm text-gray-700">{{ barn.name }}</td>
                                                    <td class="w-6.5 pr-4 py-2">
                                                        <div class="flex justify-center">
                                                            <CustomCheckbox :checked="isBarnSelected(farm, barn)" @update:checked="onBarnClicked(farm, barn)"></CustomCheckbox>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </ExpandCollapseTransition>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import ExpandIcon from '@/components/icons/ExpandIcon.vue';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';
import { ReportBatch, ReportLocation, ReportLocationBarn, ReportViewState } from '@/types';

export default defineComponent({
    components: { CustomCheckbox, ExpandIcon, ExpandCollapseTransition },
    props: {
        state: {
            type: Object as () => ReportViewState,
            required: true,
        },
        selectableSites: {
            type: Array as () => (ReportLocation | ReportBatch)[],
            default: () => [],
        },
        batchTabIsActive: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['update:state'],
    data() {
        return {
            expandedFarmKeys: [] as string[],
        };
    },
    computed: {
        computedState: {
            get(): ReportViewState {
                return this.$props.state;
            },
            set(newValue: ReportViewState) {
                this.$emit('update:state', newValue);
            },
        },
        batchSites(): ReportBatch[] {
            return this.batchTabIsActive ? (this.$props.selectableSites as ReportBatch[]) : [];
        },
        farmSites(): ReportLocation[] {
            return this.batchTabIsActive ? [] : (this.$props.selectableSites as ReportLocation[]);
        },
        selectedFarmKeys(): string[] {
            return this.computedState.selectedReportLocationKeys;
        },
        selectedBarnKeys(): string[] {
            return this.computedState.selectedReportLocationBarnKeys;
        },
        farmKeyIdMap(): Record<string, number> {
            const map: Record<string, number> = {};
            this.farmSites.forEach((farm) => {
                map[this.getFarmKey(farm)] = farm.id;
            });
            return map;
        },
        barnKeyIdMap(): Record<string, number> {
            const map: Record<string, number> = {};
            this.farmSites.forEach((farm) => {
                (farm.barns || []).forEach((barn) => {
                    map[this.getBarnKey(farm, barn)] = barn.id;
                });
            });
            return map;
        },
    },
    methods: {
        getFarmKey(farm: ReportLocation) {
            return `${farm.id}::${farm.animal_group_name ?? 'no-group'}`;
        },
        getBarnKey(farm: ReportLocation, barn: ReportLocationBarn) {
            return `${this.getFarmKey(farm)}::barn::${barn.id}`;
        },
        isFarmExpanded(farm: ReportLocation) {
            return this.expandedFarmKeys.includes(this.getFarmKey(farm));
        },
        toggleFarmExpanded(farm: ReportLocation) {
            const farmKey = this.getFarmKey(farm);
            const index = this.expandedFarmKeys.indexOf(farmKey);
            if (index >= 0) {
                this.expandedFarmKeys.splice(index, 1);
            } else {
                this.expandedFarmKeys.push(farmKey);
            }
        },
        isFarmSelected(farm: ReportLocation) {
            return this.selectedFarmKeys.includes(this.getFarmKey(farm));
        },
        isFarmChecked(farm: ReportLocation) {
            const barns = farm.barns || [];
            if (barns.length === 0) {
                return this.isFarmSelected(farm);
            }
            return barns.every((barn) => this.isBarnSelected(farm, barn));
        },
        isFarmIndeterminate(farm: ReportLocation) {
            const barns = farm.barns || [];
            if (barns.length === 0) {
                return false;
            }
            const selectedCount = barns.filter((barn) => this.isBarnSelected(farm, barn)).length;
            return selectedCount > 0 && selectedCount < barns.length;
        },
        isBarnSelected(farm: ReportLocation, barn: ReportLocationBarn) {
            return this.selectedBarnKeys.includes(this.getBarnKey(farm, barn));
        },
        removeSelection(list: string[], key: string) {
            const index = list.indexOf(key);
            if (index >= 0) {
                list.splice(index, 1);
            }
        },
        syncSiteIdsFromSelection() {
            const selectedIds = new Set<number>();

            this.selectedBarnKeys.forEach((barnKey) => {
                const barnId = this.barnKeyIdMap[barnKey];
                if (barnId !== undefined) {
                    selectedIds.add(barnId);
                }
            });

            this.computedState.siteIds.splice(0, this.computedState.siteIds.length, ...Array.from(selectedIds));
        },
        onBatchClicked(site: ReportBatch) {
            const siteId = site.site_ids;
            const indexOfSiteId = this.computedState.siteIds.indexOf(siteId);
            if (indexOfSiteId >= 0) {
                this.computedState.siteIds.splice(indexOfSiteId, 1);
            } else {
                this.computedState.siteIds.push(siteId);
            }
        },
        onFarmClicked(farm: ReportLocation) {
            const farmKey = this.getFarmKey(farm);
            const isChecked = this.isFarmChecked(farm);

            if (isChecked) {
                // All barns selected - deselect all
                this.removeSelection(this.selectedFarmKeys, farmKey);
                (farm.barns || []).forEach((barn) => {
                    this.removeSelection(this.selectedBarnKeys, this.getBarnKey(farm, barn));
                });
            } else {
                // No barns or some barns selected - select all
                if (!this.selectedFarmKeys.includes(farmKey)) {
                    this.selectedFarmKeys.push(farmKey);
                }
                (farm.barns || []).forEach((barn) => {
                    const barnKey = this.getBarnKey(farm, barn);
                    if (!this.selectedBarnKeys.includes(barnKey)) {
                        this.selectedBarnKeys.push(barnKey);
                    }
                });
            }
            this.syncSiteIdsFromSelection();
        },
        onBarnClicked(farm: ReportLocation, barn: ReportLocationBarn) {
            const barnKey = this.getBarnKey(farm, barn);
            if (this.selectedBarnKeys.includes(barnKey)) {
                this.removeSelection(this.selectedBarnKeys, barnKey);
            } else {
                this.selectedBarnKeys.push(barnKey);
            }

            // Sync farm selection state based on barn selections
            const farmKey = this.getFarmKey(farm);
            const allBarnsSelected = (farm.barns || []).every((b) => this.selectedBarnKeys.includes(this.getBarnKey(farm, b)));
            if (allBarnsSelected && !this.selectedFarmKeys.includes(farmKey)) {
                this.selectedFarmKeys.push(farmKey);
            } else if (!allBarnsSelected && this.selectedFarmKeys.includes(farmKey)) {
                this.removeSelection(this.selectedFarmKeys, farmKey);
            }

            this.syncSiteIdsFromSelection();
        },
    },
});
</script>
